<template>
  <b-modal
    v-model="visible"
    size="lg"
    centered
    scrollable
    body-class="py-3"
    footer-class="border-top"
    hide-header-close
    @hidden="onHidden"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center justify-content-between w-100">
        <span class="text-muted font-weight-medium h6 mb-0">Detalhes da ação</span>
        <div class="d-flex align-items-center text-right">
          <b-badge v-if="statusTitle" pill :variant="statusVariant" class="font-weight-normal mr-2">
            {{ statusTitle }}
          </b-badge>
          <b-button variant="link" size="sm" class="p-0 text-primary flex-shrink-0" @click="close">
            <font-awesome-icon :icon="['fas', 'times']" class="fa-lg" />
          </b-button>
        </div>
      </div>
    </template>

    <b-alert v-if="!state.loading && !state.item" show variant="warning" class="mb-0">
      Não foi possível localizar esta ação.
    </b-alert>

    <div v-else>
      <action-base-details :list-details="normalizedItem" :show-title="false" />

      <div v-if="childItems.length" class="sub-actions-section mt-4">
        <div class="d-flex align-items-center mb-3">
          <font-awesome-icon :icon="['fas', 'sitemap']" class="mr-2 text-primary" />
          <span class="text-muted font-weight-medium h6 mb-0">
            Subações ({{ childItems.length }})
          </span>
        </div>
        <ul class="sub-actions-list list-unstyled mb-0">
          <li
            v-for="child in childItems"
            :key="child.id"
            class="sub-action-list-item"
          >
            <span class="sub-action-name">{{ child.what ?? child.name ?? 'Subação' }}</span>
            <div class="sub-action-item-right">
              <b-badge
                pill
                :variant="getStatusColor(child.status)"
                class="font-weight-normal sub-action-badge"
              >
                {{ getStatusTitle(child.status) }}
              </b-badge>
              <b-button
                size="sm"
                pill
                variant="outline-primary"
                class="sub-action-btn-view"
                title="Visualizar"
                @click="viewChild(child)"
              >
                <font-awesome-icon :icon="['far', 'eye']" class="mr-1" />
                <span class="sub-action-btn-text">Visualizar</span>
              </b-button>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <template #modal-footer>
      <b-button variant="dark" @click="close">Fechar</b-button>
    </template>
  </b-modal>
</template>

<script>
import ActionBaseDetails from "@/modules/action_plans/components/ActionBaseDetails.vue";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "ModalShowAction",
  components: { ActionBaseDetails },
  emits: ["closed"],
  data() {
    return {
      visible: false,
      itemId: null,
      planStructureId: null,
      actionPlanId: null,
      state: {
        loading: false,
        item: null,
        allItems: [],
      },
    };
  },
  computed: {
    normalizedItem() {
      const item = this.state.item || {};
      return {
        ...item,
        what: item.what ?? item.name ?? "",
        why: item.why ?? "",
        where: item.where ?? "",
        who: item.who?.name ?? item.who ?? "",
        when_start: item.when_start ?? item.when ?? item.whenStart ?? "",
        how: item.how ?? "",
        how_much: item.how_much ?? item.howMuch ?? item.cost ?? "",
      };
    },
    childItems() {
      if (!this.state.item?.id) return [];
      const currentId = String(this.state.item.id);
      return this.state.allItems.filter((i) => String(i.parent_id) === currentId);
    },
    statusKey() {
      return this.GetStatusKey?.(this.normalizedItem?.status) ?? String(this.normalizedItem?.status || "").toLowerCase();
    },
    statusTitle() {
      return this.mappedStatus[this.statusKey]?.title || "";
    },
    statusVariant() {
      return this.mappedStatus[this.statusKey]?.color || "secondary";
    },
    mappedStatus() {
      return this.GetMappedStatus?.() ?? {};
    },
  },
  methods: {
    ...mapActions("action_plans", ["ActionGetAllItems"]),
    ...mapGetters("action_plans", ["GetMappedStatus", "GetStatusKey"]),
    show({ itemId, planStructureId, actionPlanId }) {
      this.itemId = itemId;
      this.planStructureId = planStructureId || null;
      this.actionPlanId = actionPlanId || null;
      this.visible = true;
      this.loadItem();
    },
    close() {
      this.visible = false;
    },
    onHidden() {
      this.state.item = null;
      this.state.allItems = [];
      this.itemId = null;
      this.$emit("closed");
    },
    getStatusTitle(status = "") {
      const key = this.GetStatusKey?.(status) ?? String(status || "").toLowerCase();
      return this.mappedStatus[key]?.title ?? "Aguardando";
    },
    getStatusColor(status = "") {
      const key = this.GetStatusKey?.(status) ?? String(status || "").toLowerCase();
      return this.mappedStatus[key]?.color ?? "secondary";
    },
    viewChild(child) {
      this.itemId = child.id;
      this.loadItem();
    },
    async loadItem() {
      const hasContext = this.actionPlanId || this.planStructureId;
      if (!hasContext || !this.itemId) return;
      this.state.loading = true;
      try {
        const payload = this.planStructureId
          ? { plan_structure_id: this.planStructureId }
          : { id: this.actionPlanId };
        const result = await this.ActionGetAllItems(payload);
        const items = Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : [];
        this.state.allItems = items;
        this.state.item = items.find((i) => String(i.id) === String(this.itemId)) || null;
      } catch (e) {
        this.state.item = null;
        this.state.allItems = [];
      } finally {
        this.state.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sub-actions-section {
  border-top: 1px solid #e9ecef;
  padding-top: 1rem;
}
.sub-actions-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.sub-action-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}
.sub-action-name {
  font-size: 0.875rem;
  color: #1f2937;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0.5rem;
}
.sub-action-item-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  flex-shrink: 0;
}
.sub-action-btn-view {
  white-space: nowrap;
  flex-shrink: 0;
}
.sub-action-btn-view .sub-action-btn-text {
  white-space: nowrap;
}
.sub-action-badge {
  font-size: 0.7rem;
}

@media (max-width: 575.98px) {
  .sub-action-list-item {
    flex-direction: column;
    align-items: stretch;
  }
  .sub-action-name {
    margin-right: 0;
  }
  .sub-action-item-right {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
